import { FC, useMemo } from "react";
import { useRecoilState } from "recoil";

import { SVG_MUI } from "@ryde/assets";
import { CheckboxControlledMui, RideStationInOutIcon } from "@ryde/controls";
import { useRidesNavigation } from "@ryde/redux";
import {
  computeRideStationTypeSingle,
  IPassengerInfo,
  isRideGreyed,
  rideSelectedPassengersIdsAF,
  RideStatusEnum,
} from "@ryde/shared";

import { MapStationInfoPopupStyled } from "./map-station-info-popup.styled";

interface IMapStationInfoPopupProps {
  rideId: number;
  rideStatus: RideStatusEnum;
  stationId: number;
  fullPassengersDetails: IPassengerInfo[];
  fullAddress: string;
  addressName: string;
}

export const MapStationInfoPopup: FC<IMapStationInfoPopupProps> = ({
  fullPassengersDetails,
  addressName,
  fullAddress,
  stationId,
  rideId,
  rideStatus,
}) => {
  const isGreyed = isRideGreyed({ status: rideStatus });
  const [selectedPassIds, setSelectedPassIds] = useRecoilState(
    rideSelectedPassengersIdsAF(rideId || 0),
  );

  const { isGridMultiSelectView, isGridSingleSelectView } =
    useRidesNavigation();

  const togglePassengerSelected = (passId: number) => () => {
    setSelectedPassIds(selectedIds => {
      const selectedIdsSet = new Set(selectedIds);
      if (!selectedIdsSet.has(passId)) {
        return [...selectedIds, passId];
      } else {
        return selectedIds.filter(x => x !== passId);
      }
    });
  };

  const isCheckboxEnabled = useMemo(
    () => !isGreyed && (isGridMultiSelectView || isGridSingleSelectView),
    [isGreyed, isGridMultiSelectView, isGridSingleSelectView],
  );

  const selectedPassIdsSet = useMemo(
    () => (selectedPassIds ? new Set(selectedPassIds) : new Set<number>()),
    [selectedPassIds],
  );

  return (
    <MapStationInfoPopupStyled>
      <div className="station-info-header" title={fullAddress}>
        {addressName}
      </div>

      <div className="content">
        {fullPassengersDetails?.map((passenger, idx) => (
          <div className="passenger-row" key={`${passenger.id}_${idx}`}>
            {isCheckboxEnabled && (
              <CheckboxControlledMui
                checked={selectedPassIdsSet.has(passenger.id)}
                setCheckedToggle={togglePassengerSelected?.(passenger.id)}
                width={15}
                height={15}
              />
            )}

            <RideStationInOutIcon
              statusType={computeRideStationTypeSingle(passenger, stationId)}
              isGreyed={isGreyed}
            />

            <div className="passenger-name">
              {passenger.firstName} {passenger.lastName}
            </div>

            {passenger.hasDog && (
              <span className="has-dog">
                <SVG_MUI.DogNoCircle4SVG />
              </span>
            )}
          </div>
        ))}
      </div>
    </MapStationInfoPopupStyled>
  );
};
